import { useState, useEffect, type ElementType } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { ModuleProperties } from '@/lib/core/types';
import { HomePageWhatsOnElementModel, EventListItemDto } from '@/services/platform/api/model';
import { getWhatsOn } from '@/services/platform/data/events';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { LinksToLinkMap, ImagesToImageMap } from '@/lib/core/utils';
import { cn } from '@/lib/core/utils/cn';
import { Heading } from '@/components/ui/heading';
import { Button } from '@/components/ui/button';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { useEventCategoryLabel } from '@/components/shared/events/list/categories';

/** Number of upcoming events surfaced on the home page "What's On" carousel. */
const MAX_WHATS_ON = 6;

// ── MaxCard ──────────────────────────────────────────────────────────────────

function MaxCard({ event, onClick }: { event: EventListItemDto; onClick: () => void }) {
  const getCategoryLabel = useEventCategoryLabel();
  const { appSettings } = useApp();
  const eyebrow = [event.eventCategory ? getCategoryLabel(event.eventCategory) : undefined, event.venueName]
    .filter(Boolean)
    .join(' • ');

  // Fall back to the CMS-configured no-pic image if the event has none or it fails to load;
  // no placeholder span here — a missing/failed image just shows the gradient over nothing.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [event.imageUrl]);
  const showImage = !!event.imageUrl && !imageFailed;
  const fallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  return (
    <article
      className="relative flex h-130 w-full flex-col items-center justify-center overflow-hidden py-2.5"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {showImage ? (
          <img
            src={event.imageUrl ?? undefined}
            alt=""
            className="absolute inset-0 size-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          fallbackImageUrl && (
            <img
              src={fallbackImageUrl}
              alt=""
              className="absolute inset-0 size-full object-cover"
              loading="lazy"
            />
          )
        )}
        <div className="absolute inset-0 bg-linear-to-b from-[rgba(5,28,28,0.24)] via-[rgba(0,0,0,0.7)] via-[56.827%] to-[rgba(5,28,28,0)]" />
      </div>

      <div className="relative flex w-full flex-col items-center gap-6 text-center text-white">
        {eyebrow && <span className="font-pp-neue-montreal text-[14px] italic">{eyebrow}</span>}
        <span className="font-didot text-[36px] leading-[normal] font-semibold tracking-[0.5px]">
          {event.title ?? '—'}
        </span>
      </div>
    </article>
  );
}

// ── HomePageWhatsOn ────────────────────────────────────────────────────────

export const HomePageWhatsOn = (props: ModuleProperties) => {
  const content = props.content as HomePageWhatsOnElementModel | undefined;
  const properties = content?.properties;
  const title = properties?.title ?? "What's On";
  const headingAs = (properties?.titleHeading?.toLowerCase() ?? 'h2') as ElementType;
  const ctaLink = LinksToLinkMap(properties?.link ?? undefined);
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();

  const [events, setEvents] = useState<EventListItemDto[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // `/events/whats-on`, not the listing: it resolves each event's real next date, so a
    // recurring campaign takes its chronological place instead of being clamped to "today"
    // alongside every other campaign, and occupies one slot rather than repeating.
    // excludeExternal: true — external events (ticketed on partner sites) show only in the
    // calendar, not in the general What's On carousel.
    // includeRestricted: true — the rail shows Members Only / Invite Only cards too, as the
    // calendar does; the card renders them locked rather than hiding them from the carousel.
    getWhatsOn({ limit: MAX_WHATS_ON, excludeExternal: true, includeRestricted: true }, locale)
      .then(result => {
        if (!cancelled) setEvents(result.slice(0, MAX_WHATS_ON));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (events.length === 0) return null;

  return (
    <ModuleWrapper
      component={props}
      isFullWidth={true}
      classNames={{
        base: 'module__whats-on',
        wrapper: 'py-0 lg:py-0',
        grid: 'grid-cols-1 lg:grid-cols-1 gap-0',
      }}
    >
      <div className="col-span-1 flex flex-col gap-6 px-4 py-6">
        <Heading as={headingAs} className="font-pp-neue-montreal text-[24px] font-semibold text-white">
          {title}
        </Heading>

        <div className="-mx-4 w-[calc(100%+32px)] overflow-hidden" aria-label={title}>
          <Swiper
            slidesPerView={1}
            onActiveIndexChange={swiper => setActiveIndex(swiper.activeIndex)}
            onReachEnd={() => setActiveIndex(events.length - 1)}
          >
            {events.map(event => {
              const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;
              const url = event.id && eventDetailPath ? `${eventDetailPath}/${event.id}` : '/';
              return (
                <SwiperSlide key={event.id}>
                  <MaxCard event={event} onClick={() => navigate(url)} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {events.length > 1 && (
          <div className="flex items-center justify-center gap-2" aria-hidden="true">
            {events.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'block h-px w-4 transition-all duration-300',
                  i === activeIndex ? 'bg-white' : 'bg-white/30'
                )}
              />
            ))}
          </div>
        )}

        {ctaLink && (
          <Button asChild variant="primary-solid" palette="white" size="lg" className="w-full">
            <Link to={ctaLink.href} target={ctaLink.target ?? '_self'}>
              {ctaLink.label}
            </Link>
          </Button>
        )}
      </div>
    </ModuleWrapper>
  );
};
